
import cn from 'classnames';
import { Link } from 'react-router';
import React, { useState, useRef, useCallback, Suspense, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import { getHighestRiskLevel } from '../constants/project';
import { HiOutlineBadgeCheck } from 'react-icons/hi';
import { BsClock } from 'react-icons/bs';

// Direct (non-lazy) imports. These were previously wrapped in lazy() + a single
// Suspense boundary, but under React StrictMode the Suspense-reveal reconnects
// the map subtree's passive effects, re-firing react-map-gl's Marker.addTo()
// against a map instance that is mid-teardown — which throws in mapbox-gl and
// crashed /map. Importing them normally removes that Suspense reconnect cycle.
import {
  Map,
  Marker,
  Source,
  Layer,
  NavigationControl,
  FullscreenControl,
  GeolocateControl,
} from 'react-map-gl/mapbox';

// Mapbox Error Boundary Component
//
// A real React error boundary (getDerivedStateFromError) — NOT just a window
// 'error' listener. react-map-gl can throw during a marker's mount effect when
// the underlying map gets torn down mid-cycle (e.g. React StrictMode double-
// invokes effects in dev, destroying the map between mount and addTo()). Those
// errors surface in React's commit phase and must be caught by an error
// boundary; a functional component with a window listener cannot catch them, so
// they previously propagated to the app-level boundary and crashed the whole
// page. This keeps any map failure contained to the map area. In production
// (no StrictMode) the map mounts once and this stays transparent.
class MapboxErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
    this.handleWindowError = this.handleWindowError.bind(this);
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    console.error('❌ Map error caught by boundary:', error);
  }

  componentDidMount() {
    // Preserve prior behaviour: also catch async mapbox / dynamic-import
    // failures that fire as window 'error' events outside React's commit phase.
    window.addEventListener('error', this.handleWindowError);
  }

  componentWillUnmount() {
    window.removeEventListener('error', this.handleWindowError);
  }

  handleWindowError(event: ErrorEvent) {
    if (
      event.message &&
      (event.message.includes('mapbox') ||
        event.message.includes('Failed to fetch dynamically imported module'))
    ) {
      console.error('❌ Mapbox loading error:', event);
      this.setState({ hasError: true });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="w-full h-full bg-[#f5f5f5] flex items-center justify-center">
          <div className="text-center">
            <div className="text-[#888] text-lg mb-2">Map Loading Error</div>
            <div className="text-[#aaa] text-sm">Please refresh the page to try again</div>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-[#171717] text-white rounded hover:bg-[#171717]"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

import 'maplibre-gl/dist/maplibre-gl.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import './Map.css';
import { MAPBOX_TOKEN, MAPBOX_STYLE_URL } from '../config/mapbox';
import { MapPopup } from './MapPopup';
import { useMapResize, type ResizableMap } from '../hooks/useMapResize';
import { TaskRisksWidget } from './TaskRisksWidget';
import { getRiskTailwindClasses } from '../styles/designSystem';

const VIEWPORT = {
  latitude: 39.8283,  // Center of USA
  longitude: -98.5795,  // Center of USA
  zoom: 3.5,  // Zoom level to show entire USA width
  maxZoom: 18,  // Increased max zoom for better detail
  minZoom: 2,
  bearing: 0,  // Map rotation
  pitch: 0,    // Map tilt
};

// Function to extract city from site - prioritize Google Address data
const getCityFromSite = (site: any): string => {
  if (!site) return '';
  
  // First, try to use structured Google Address data with new fields
  if (site.google_address_data) {
    const { city, state, locality, administrative_area_level_1 } = site.google_address_data;
    
    // Use locality (city name) from Google Address Data
    if (locality && administrative_area_level_1) {
      return `${locality}, ${administrative_area_level_1}`;
    }
    if (locality) {
      return locality;
    }
    
    // Fallback to old fields for backward compatibility
    if (city && state) {
      return `${city}, ${state}`;
    }
    if (city) {
      return city;
    }
  }
  
  // Fallback to parsing the address string (for backward compatibility)
  const address = site.address;
  if (!address) return '';
  
  // Split address by comma and trim whitespace
  const parts = address.split(',').map((part: string) => part.trim());
  
  // Look for the city part (usually the second part, but not always)
  if (parts.length >= 2) {
    for (let i = 1; i < parts.length; i++) {
      const part = parts[i];
      // Skip if it looks like a state code (2 letters) or zip code (numbers)
      if (!/^\d+$/.test(part) && !/^[A-Z]{2}$/.test(part) && part.length > 2) {
        // Additional check: if this part contains a zip code, extract just the city
        const cityPart = part.split(' ')[0]; // Take first word if it contains zip
        if (cityPart && cityPart.length > 2) {
          return cityPart;
        }
        return part;
      }
    }
    // Fallback to second part if no clear city found
    return parts[1];
  }
  
  // If only one part, return it (might be just city name)
  return parts[0];
};

// Function to get project's highest risk level from plan items and action items
const getProjectRiskLevel = (project: any): string => {
  const allItems = [
    ...(project.items || []),
    ...(project.actionItems || [])
  ];

  if (allItems.length === 0) {
    return 'none';
  }

  const riskLevels = allItems.map((item: any) => item.risks || 'none');
  return getHighestRiskLevel(riskLevels).toLowerCase();
};

// Function to determine project color based on risk level using design system
const getProjectColor = (project: any): string => {
  const riskLevel = getProjectRiskLevel(project);
  const riskClasses = getRiskTailwindClasses(riskLevel);
  return riskClasses.bg;
};

// Function to get adaptive thresholds based on zoom level
const getAdaptiveThresholds = (zoom: number) => {
  // Adaptive thresholds based on zoom level
  if (zoom <= 4) {
    // Very zoomed out - large distances needed
    return {
      overlapThreshold: 0.5,  // 50km threshold
      spreadRadius: 0.15      // 15km spread radius
    };
  } else if (zoom <= 6) {
    // Zoomed out - medium distances
    return {
      overlapThreshold: 0.2,  // 20km threshold
      spreadRadius: 0.08      // 8km spread radius
    };
  } else if (zoom <= 8) {
    // Medium zoom - smaller distances
    return {
      overlapThreshold: 0.1,  // 10km threshold
      spreadRadius: 0.04      // 4km spread radius
    };
  } else if (zoom <= 10) {
    // More zoomed in - even smaller distances
    return {
      overlapThreshold: 0.05, // 5km threshold
      spreadRadius: 0.02      // 2km spread radius
    };
  } else {
    // Very zoomed in - small distances
    return {
      overlapThreshold: 0.01, // 1km threshold
      spreadRadius: 0.008     // 0.8km spread radius
    };
  }
};

// Function to spread overlapping pins with adaptive zoom-based spacing
const spreadOverlappingPins = (servers: any[], zoom: number = 3.5) => {
  const { overlapThreshold, spreadRadius } = getAdaptiveThresholds(zoom);
  
  // Filter servers with valid locations and IDs
  const validServers = servers.filter(server => server.sites?.[0]?.geo_location && server.id);
  
  // Group servers by proximity
  const groups: any[][] = [];
  const processed = new Set<string>();
  
  validServers.forEach(server => {
    if (!server.id || processed.has(server.id)) return;
    
    const [lat, lng] = server.sites![0].geo_location;
    const group = [server];
    processed.add(server.id);
    
    // Find other servers within the overlap threshold
    validServers.forEach(otherServer => {
      if (!otherServer.id || processed.has(otherServer.id)) return;
      
      const [otherLat, otherLng] = otherServer.sites![0].geo_location;
      const distance = Math.sqrt(
        Math.pow(lat - otherLat, 2) + Math.pow(lng - otherLng, 2)
      );
      
      if (distance < overlapThreshold) {
        group.push(otherServer);
        processed.add(otherServer.id);
      }
    });
    
    groups.push(group);
  });
  
  // Spread pins in each group
  const spreadServers = groups.flatMap(group => {
    if (group.length === 1) {
      return group; // No need to spread single pins
    }
    
    // Calculate center point of the group
    const centerLat = group.reduce((sum, s) => sum + s.sites![0].geo_location[0], 0) / group.length;
    const centerLng = group.reduce((sum, s) => sum + s.sites![0].geo_location[1], 0) / group.length;
    
    // Spread pins around the center in a circle
    return group.map((server, index) => {
      const angle = (2 * Math.PI * index) / group.length;
      const offsetLat = spreadRadius * Math.cos(angle);
      const offsetLng = spreadRadius * Math.sin(angle);
      
      return {
        ...server,
        spreadCoordinates: {
          latitude: centerLat + offsetLat,
          longitude: centerLng + offsetLng,
          originalLatitude: server.sites![0].geo_location[0],
          originalLongitude: server.sites![0].geo_location[1],
          isSpread: true
        }
      };
    });
  });
  
  return spreadServers;
};

export const MapPage = ({ showMenu: _showMenu, servers, riskServers, riskCounts, riskFilter, onRiskFilter }: {showMenu?: boolean, servers: any[], riskServers?: any[], riskCounts?: Record<string, number>, riskFilter?: string, onRiskFilter?: (riskLevel: string) => void}) => {
  const [selected, setSelected] = useState<string|undefined>(undefined);
  const [currentZoom, setCurrentZoom] = useState<number>(VIEWPORT.zoom);
  const mapRef = useRef<any>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const watchMapResize = useMapResize();

  React.useEffect(() => {
    setIsMapLoaded(true);
  }, []);
  
  // Close popup when risk filter changes
  React.useEffect(() => {
    setSelected(undefined);
  }, [riskFilter]);

  // Handle zoom changes
  const handleZoomChange = useCallback((evt: any) => {
    const zoom = evt.viewState.zoom;
    setCurrentZoom(zoom);
  }, []);

  // Handle map error
  const handleMapError = useCallback((error: any) => {
    const inner = error?.error ?? error;
    console.error('🗺️ Map error:', {
      message: inner?.message,
      status: inner?.status,
      url: inner?.url,
    });
  }, []);


  
  // Risk filtering happens on the backend: riskServers are the projects matching the selected level.
  const filteredServers = useMemo(() => {
    if (!riskFilter || riskFilter === 'all') {
      return servers;
    }
    return riskServers ?? [];
  }, [servers, riskServers, riskFilter]);
  
  // Fit map to show all visible projects/sites
  const fitToVisibleProjects = useCallback(() => {
    try {
      const map = mapRef.current?.getMap ? mapRef.current.getMap() : mapRef.current;
      if (!map || typeof map.fitBounds !== 'function') return;

      const bounds = new mapboxgl.LngLatBounds();
      (filteredServers || []).forEach((s) => {
        if (s?.sites?.length) {
          s.sites.forEach((site: any) => {
            const geo = site.geo_location;
            if (Array.isArray(geo) && geo.length >= 2 && typeof geo[0] === 'number' && typeof geo[1] === 'number') {
              // geo_location is [lat, lng]
              bounds.extend([geo[1], geo[0]]);
            } else if (site.longitude && site.latitude) {
              bounds.extend([site.longitude, site.latitude]);
            }
          });
        } else if (s.longitude && s.latitude) {
          bounds.extend([s.longitude, s.latitude]);
        }
      });

      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, { padding: 100, maxZoom: 6 });
      }
    } catch (e) {
      console.warn('🗺️ Failed to fit bounds:', e);
    }
  }, [filteredServers]);

  // Handle map load
  const handleMapLoad = useCallback((evt: { target: ResizableMap }) => {
    console.log('🗺️ Map loaded successfully');
    console.log('🎛️ Navigation controls should be visible');
    watchMapResize(evt.target);
    fitToVisibleProjects();
  }, [fitToVisibleProjects, watchMapResize]);

  // Process servers to spread overlapping pins with current zoom
  const spreadServers = spreadOverlappingPins(filteredServers, currentZoom);

  // Get visible project IDs from ALL servers (unfiltered) for the risk widget
  // This ensures the widget always counts all tasks from visible projects, regardless of risk filter
  const allSpreadServers = spreadOverlappingPins(servers, currentZoom);
  const visibleProjectIds = allSpreadServers.map((s: any) => s.id);
  
  // Refocus when filters change and map already loaded
  React.useEffect(() => {
    if (isMapLoaded) {
      fitToVisibleProjects();
    }
  }, [fitToVisibleProjects, isMapLoaded]);

  return (
    <div className={selected ? 'map-has-popup' : ''}>
      {selected && filteredServers.find((server) => server.id === selected) && (
        <MapPopup
          projectData={filteredServers.find((server) => server.id === selected)!}
          riskFilter={riskFilter}
          onClose={() => setSelected(undefined)}
        />
      )}
      {isMapLoaded && (
        <MapboxErrorBoundary>
          <Suspense fallback={
            <div className="w-full h-screen flex items-center justify-center bg-[#fafafa]">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#171717] mx-auto mb-4"></div>
                <p className="text-[#555]">Loading map...</p>
              </div>
            </div>
          }>
          <Map
            ref={mapRef}
            initialViewState={VIEWPORT}
            style={{width: '100%', height: '100vh'}}
            mapboxAccessToken={MAPBOX_TOKEN}
            mapStyle={MAPBOX_STYLE_URL}
            onClick={() => setSelected(undefined)}
            onMove={handleZoomChange}
            onLoad={handleMapLoad}
            onError={handleMapError}
          >
            {/* Navigation Controls - Zoom in/out, compass */}
            <NavigationControl 
              position="bottom-right"
              showCompass={false}
              showZoom={true}
              visualizePitch={false}
            />
            
            {/* Fullscreen Control */}
            <FullscreenControl position="bottom-right" />
            
            {/* Geolocate Control - Find my location */}
            <GeolocateControl 
              position="bottom-right"
              trackUserLocation={true}
              showUserHeading={true}
              showAccuracyCircle={true}
              onGeolocate={(e) => {
                console.log('📍 User location:', e.coords);
              }}
            />
            

        {spreadServers.map((server) => {
          if (!server.sites?.[0]?.geo_location) return null;

          // Use filter color if filter is active, otherwise use project's risk color
          const projectColor = riskFilter && riskFilter !== 'all' 
            ? getRiskTailwindClasses(riskFilter).bg
            : getProjectColor(server);
          
          const defaultSite = server.sites?.find((site: any) => site.is_default) || server.sites?.[0];
          
          // Validate coordinates before using them
          if (!defaultSite?.geo_location || !Array.isArray(defaultSite.geo_location) || defaultSite.geo_location.length < 2) {
            console.warn('Invalid coordinates for server:', server.name, defaultSite?.geo_location);
            return null;
          }
          
          const [defaultLat, defaultLng] = defaultSite.geo_location;
          
          // Check if coordinates are valid numbers
          if (typeof defaultLat !== 'number' || typeof defaultLng !== 'number' || 
              isNaN(defaultLat) || isNaN(defaultLng)) {
            console.warn('Invalid coordinate values for server:', server.name, { defaultLat, defaultLng });
            return null;
          }
          
          // Use spread coordinates if available, otherwise use original coordinates
          const coordinates = (server as any).spreadCoordinates
            ? {
                latitude: (server as any).spreadCoordinates.latitude,
                longitude: (server as any).spreadCoordinates.longitude
              }
            : {
                latitude: defaultLat,
                longitude: defaultLng
              };
          
          // Final validation of coordinates
          if (typeof coordinates.latitude !== 'number' || typeof coordinates.longitude !== 'number' ||
              isNaN(coordinates.latitude) || isNaN(coordinates.longitude)) {
            console.warn('Final coordinates validation failed for server:', server.name, coordinates);
            return null;
          }

          return (
            <React.Fragment key={server.id}>
              {/* Connection line for spread pins */}
              {(server as any).spreadCoordinates && 
               typeof (server as any).spreadCoordinates.originalLatitude === 'number' &&
               typeof (server as any).spreadCoordinates.originalLongitude === 'number' &&
               !isNaN((server as any).spreadCoordinates.originalLatitude) &&
               !isNaN((server as any).spreadCoordinates.originalLongitude) && (
                <>
                  {/* Original location marker */}
                  <Marker
                    longitude={(server as any).spreadCoordinates.originalLongitude}
                    latitude={(server as any).spreadCoordinates.originalLatitude}
                    anchor="center"
                  >
                    <div className="w-2 h-2 bg-[#bbb] rounded-full opacity-60"></div>
                  </Marker>
                  
                  {/* Connection line using Source and Layer */}
                  <Source
                    id={`line-${server.id}`}
                    type="geojson"
                    data={{
                      type: 'Feature',
                      geometry: {
                        type: 'LineString',
                        coordinates: [
                          [(server as any).spreadCoordinates.originalLongitude, (server as any).spreadCoordinates.originalLatitude],
                          [coordinates.longitude, coordinates.latitude]
                        ]
                      },
                      properties: {}
                    }}
                  >
                    <Layer
                      id={`line-layer-${server.id}`}
                      type="line"
                      paint={{
                        'line-color': 'rgba(128, 128, 128, 0.6)',
                        'line-width': 1,
                        'line-dasharray': [2, 2]
                      }}
                    />
                  </Source>
                </>
              )}
              
              {/* Main pin marker */}
              <Marker
                longitude={coordinates.longitude}
                latitude={coordinates.latitude}
                anchor="bottom"
              >
                <Link
                  to={`/projects/${server.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelected(server.id)
                  }}
                  className={
                    cn(
                      "marker bg-white rounded-full shadow-[0_2px_8px_rgba(0,0,0,0.06)] flex justify-start items-center p-1 gap-1 hover:bg-[#F0F1F4] transition-all"
                    )
                  }
                >
                  <div className={cn('w-10 h-10 rounded-full text-white flex justify-center items-center text-xl', projectColor)}>
                    {riskFilter && riskFilter !== 'all'
                      ? (riskCounts?.[server.id] ?? 0)
                      : (() => {
                          if (server.status === 'NOT_STARTED') return <HiOutlineBadgeCheck size={25} />;
                          if (server.status === 'COMPLETED') return <BsClock size={25} />;
                          if (server.status === 'IN_PROGRESS') return <HiOutlineBadgeCheck size={25} />;
                          return <HiOutlineBadgeCheck size={25} />;
                        })()
                    }
                  </div>
                  <div className='leading-tight pr-3'>
                    <div className='text-base font-medium font-[system-ui,-apple-system,sans-serif]'>{server.name}</div>
                    <div className='text-xs text-[#888] font-[system-ui,-apple-system,sans-serif]'>{getCityFromSite(defaultSite)}</div>
                  </div>
                </Link>
              </Marker>
            </React.Fragment>
          )}
        )}
          </Map>
        </Suspense>
        </MapboxErrorBoundary>
      )}
      
      {/* Task Risks Widget positioned in bottom left corner */}
      <div className="absolute bottom-4 left-4 z-50">
        <TaskRisksWidget projects={servers} visibleProjectIds={visibleProjectIds} riskFilter={riskFilter} onRiskFilter={onRiskFilter} />
      </div>
    </div>
  );
}
